import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Select,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  Button,
  Collapse,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react"
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"

const DormantStock = ({ supplier, retailer }) => {
  const [daysThreshold, setDaysThreshold] = useState(90)
  const [loading, setLoading] = useState(false)
  const [kpiData, setKpiData] = useState(null)
  const [summaryData, setSummaryData] = useState([])
  const [detailData, setDetailData] = useState([])
  const [showDetail, setShowDetail] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer, daysThreshold])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const body = {
        supplier,
        retailer,
        userid: localStorage.getItem("userid"),
        daysThreshold,
      }

      const [kpiRes, summaryRes] = await Promise.all([
        axios.post(`${process.env.REACT_APP_API_URL}/insights/dormant-stock/kpi`, body, { signal }),
        axios.post(`${process.env.REACT_APP_API_URL}/insights/dormant-stock/summary`, body, { signal }),
      ])

      if (kpiRes.data.status === 1) setKpiData(kpiRes.data.data)
      if (summaryRes.data.status === 1) setSummaryData(summaryRes.data.data)
      else setSummaryData([])
    } catch (err) {
      if (!axios.isCancel(err)) console.error("DormantStock fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDetail = async () => {
    if (detailData.length > 0) {
      setShowDetail(!showDetail)
      return
    }
    try {
      const body = {
        supplier,
        retailer,
        userid: localStorage.getItem("userid"),
        daysThreshold,
      }
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/insights/dormant-stock/detail`, body)
      if (res.data.status === 1) {
        setDetailData(res.data.data)
        setShowDetail(true)
      }
    } catch (err) {
      console.error("DormantStock detail error:", err)
    }
  }

  // Reset detail when threshold changes
  useEffect(() => {
    setDetailData([])
    setShowDetail(false)
  }, [daysThreshold, supplier, retailer])

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const formatDate = (val) => {
    if (!val) return "Never"
    return new Date(val).toLocaleDateString("en-ZA")
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      // Fetch detail data if not already loaded
      let detail = detailData
      if (detail.length === 0) {
        const body = {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
          daysThreshold,
        }
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/insights/dormant-stock/detail`, body)
        if (res.data.status === 1) {
          detail = res.data.data
          setDetailData(detail)
        }
      }

      const workbook = new ExcelJS.Workbook()

      // Sheet 1: Summary by Article
      if (summaryData.length > 0) {
        const ws1 = workbook.addWorksheet("Dormant Summary")
        ws1.columns = [
          { header: "Article Code", key: "article_code", width: 15 },
          { header: "Description", key: "articledescription", width: 35 },
          { header: "Brand", key: "brand", width: 18 },
          { header: "# Stores", key: "store_count", width: 10 },
          { header: "SOH Qty", key: "total_soh_qty", width: 12 },
          { header: "SOH Value", key: "total_soh_value", width: 15 },
          { header: "Unit Cost", key: "articleunitcost", width: 12 },
          { header: "Last Sold", key: "last_sold_date", width: 14 },
        ]
        summaryData.forEach((row) => {
          ws1.addRow({
            ...row,
            total_soh_qty: parseInt(row.total_soh_qty) || 0,
            total_soh_value: parseFloat(row.total_soh_value) || 0,
            articleunitcost: parseFloat(row.articleunitcost) || 0,
            last_sold_date: row.last_sold_date ? new Date(row.last_sold_date).toLocaleDateString("en-ZA") : "Never",
          })
        })
        // Style header
        ws1.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        // Format currency columns
        ws1.getColumn("total_soh_value").numFmt = '#,##0.00'
        ws1.getColumn("articleunitcost").numFmt = '#,##0.00'
      }

      // Sheet 2: Store-Level Detail
      if (detail.length > 0) {
        const ws2 = workbook.addWorksheet("Store Detail")
        ws2.columns = [
          { header: "Store Code", key: "site_code", width: 12 },
          { header: "Store Name", key: "site_description", width: 30 },
          { header: "Article Code", key: "article_code", width: 15 },
          { header: "Description", key: "articledescription", width: 35 },
          { header: "Brand", key: "brand", width: 18 },
          { header: "SOH Qty", key: "soh_qty", width: 10 },
          { header: "SOH Value", key: "soh_value", width: 15 },
          { header: "Last Sold", key: "date_last_sold", width: 14 },
          { header: "Last Received", key: "date_last_received", width: 14 },
          { header: "DROS", key: "dros_qty", width: 10 },
        ]
        detail.forEach((row) => {
          ws2.addRow({
            ...row,
            soh_qty: parseInt(row.soh_qty) || 0,
            soh_value: parseFloat(row.soh_value) || 0,
            dros_qty: parseFloat(row.dros_qty) || 0,
            date_last_sold: row.date_last_sold ? new Date(row.date_last_sold).toLocaleDateString("en-ZA") : "Never",
            date_last_received: row.date_last_received ? new Date(row.date_last_received).toLocaleDateString("en-ZA") : "Never",
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws2.getColumn("soh_value").numFmt = '#,##0.00'
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `DormantStock_${supplier}_${retailer}_${daysThreshold}days.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const filteredSummary = summaryData.filter(
    (row) =>
      (row.articledescription || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.article_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.brand || "").toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredDetail = detailData.filter(
    (row) =>
      (row.articledescription || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.site_description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.article_code || "").toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="center" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            Dormant Threshold
          </Text>
          <Select
            value={daysThreshold}
            onChange={(e) => setDaysThreshold(Number(e.target.value))}
            size="sm"
            w="180px"
            bg={cardBg}
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days (default)</option>
            <option value={180}>180 days (6 months)</option>
            <option value={365}>365 days (1 year)</option>
          </Select>
        </Box>
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            Search
          </Text>
          <InputGroup size="sm" maxW="300px">
            <InputLeftElement>
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search articles, stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg={cardBg}
            />
          </InputGroup>
        </Box>
        {summaryData.length > 0 && (
          <Box alignSelf="flex-end">
            <Button
              size="sm"
              leftIcon={<DownloadIcon />}
              colorScheme="teal"
              onClick={handleDownload}
              isLoading={downloading}
              loadingText="Downloading"
            >
              Download Report
            </Button>
          </Box>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : (
        <>
          {/* KPI Cards */}
          {kpiData && (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
              <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <StatLabel>Dormant Stock Value</StatLabel>
                <StatNumber fontSize="xl" color="red.500">
                  {formatCurrency(kpiData.total_dormant_value)}
                </StatNumber>
                <StatHelpText>Capital tied up in non-moving stock</StatHelpText>
              </Stat>
              <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <StatLabel>Dormant Units</StatLabel>
                <StatNumber fontSize="xl">{parseInt(kpiData.total_dormant_qty || 0).toLocaleString()}</StatNumber>
                <StatHelpText>Total units not sold in {daysThreshold}+ days</StatHelpText>
              </Stat>
              <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <StatLabel>Affected Articles</StatLabel>
                <StatNumber fontSize="xl">{parseInt(kpiData.dormant_article_count || 0)}</StatNumber>
                <StatHelpText>Distinct articles with dormant stock</StatHelpText>
              </Stat>
              <Stat bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <StatLabel>Affected Stores</StatLabel>
                <StatNumber fontSize="xl">{parseInt(kpiData.dormant_store_count || 0)}</StatNumber>
                <StatHelpText>Stores holding dormant stock</StatHelpText>
              </Stat>
            </SimpleGrid>
          )}

          {/* Summary Table */}
          {filteredSummary.length > 0 && (
            <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" mb={4}>
              <Box p={3} borderBottom="1px" borderColor={borderColor}>
                <Flex justify="space-between" align="center">
                  <Heading size="sm">Dormant Stock by Article</Heading>
                  <Badge colorScheme="red">{filteredSummary.length} articles</Badge>
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Articles with stock on hand but no sales in {daysThreshold}+ days. Highlights capital tied up in non-moving inventory that may need promotional clearance or write-off.
                </Text>
              </Box>
              <Box maxH="400px" overflowY="auto" overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Article Code</Th>
                      <Th>Description</Th>
                      <Th>Brand</Th>
                      <Th isNumeric>Stores</Th>
                      <Th isNumeric>SOH Qty</Th>
                      <Th isNumeric>SOH Value</Th>
                      <Th isNumeric>Unit Cost</Th>
                      <Th>Last Sold</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredSummary.map((row, i) => (
                      <Tr key={i} _hover={{ bg: hoverBg }}>
                        <Td fontSize="xs">{row.article_code}</Td>
                        <Td fontSize="xs" maxW="250px" isTruncated>
                          {row.articledescription}
                        </Td>
                        <Td fontSize="xs">{row.brand || "-"}</Td>
                        <Td isNumeric fontSize="xs">
                          {row.store_count}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {parseInt(row.total_soh_qty || 0).toLocaleString()}
                        </Td>
                        <Td isNumeric fontSize="xs" fontWeight="bold" color="red.500">
                          {formatCurrency(row.total_soh_value)}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {formatCurrency(row.articleunitcost)}
                        </Td>
                        <Td fontSize="xs">{formatDate(row.last_sold_date)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}

          {/* Detail Toggle */}
          {summaryData.length > 0 && (
            <Box mb={4}>
              <Button size="sm" onClick={fetchDetail} colorScheme="teal" variant="outline">
                {showDetail ? "Hide" : "Show"} Store-Level Detail{" "}
                {showDetail ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />}
              </Button>
            </Box>
          )}

          {/* Detail Table */}
          <Collapse in={showDetail}>
            {filteredDetail.length > 0 && (
              <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
                <Box p={3} borderBottom="1px" borderColor={borderColor}>
                  <Flex justify="space-between" align="center">
                    <Heading size="sm">Store-Level Dormant Stock</Heading>
                    <Badge colorScheme="orange">{filteredDetail.length} records</Badge>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Per-store breakdown showing exactly which stores are holding dormant stock, enabling targeted clearance or redistribution actions.
                  </Text>
                </Box>
                <Box maxH="450px" overflowY="auto" overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Store</Th>
                        <Th>Article</Th>
                        <Th>Brand</Th>
                        <Th isNumeric>SOH Qty</Th>
                        <Th isNumeric>SOH Value</Th>
                        <Th>Last Sold</Th>
                        <Th>Last Received</Th>
                        <Th isNumeric>DROS</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredDetail.map((row, i) => (
                        <Tr key={i} _hover={{ bg: hoverBg }}>
                          <Td fontSize="xs" maxW="200px" isTruncated>
                            {row.site_description}
                          </Td>
                          <Td fontSize="xs" maxW="200px" isTruncated>
                            {row.articledescription}
                          </Td>
                          <Td fontSize="xs">{row.brand || "-"}</Td>
                          <Td isNumeric fontSize="xs">
                            {parseInt(row.soh_qty || 0).toLocaleString()}
                          </Td>
                          <Td isNumeric fontSize="xs" color="red.500">
                            {formatCurrency(row.soh_value)}
                          </Td>
                          <Td fontSize="xs">{formatDate(row.date_last_sold)}</Td>
                          <Td fontSize="xs">{formatDate(row.date_last_received)}</Td>
                          <Td isNumeric fontSize="xs">
                            {parseFloat(row.dros_qty || 0).toFixed(1)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}
          </Collapse>

          {/* Empty state */}
          {!loading && summaryData.length === 0 && kpiData && (
            <Box textAlign="center" py={10}>
              <Text color="green.500" fontWeight="bold">
                No dormant stock found for this threshold
              </Text>
              <Text fontSize="sm" color="gray.500">
                All articles have been sold within the last {daysThreshold} days
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default DormantStock
